import { Icon } from "@mdi/react";
import { useEffect, useRef, useState } from "react";

import { mdiCheck } from "@mdi/js";
import { Badge, Tooltip } from "antd";

import { IconSpinner } from "components/icons";

import { TilesStatus } from "../../../../api/shapefile";

const PROCESSING_STATES = new Set<TilesStatus>([
  TilesStatus.Pending,
  TilesStatus.Processing,
  TilesStatus.ProcessingHighZoom,
  TilesStatus.LowZoomReady
]);

const CHECKMARK_DISPLAY_MS = 3000;

interface ShapefileStatusBadgeProps {
  tilesStatus: TilesStatus;
}

export function ShapefileStatusBadge({ tilesStatus }: ShapefileStatusBadgeProps) {
  const prevStatusRef = useRef<TilesStatus>(tilesStatus);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Cancel the timer only on unmount, not on every tilesStatus change.
  // This ensures polling updates while the checkmark is visible don't cancel it.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = tilesStatus;

    if (PROCESSING_STATES.has(prev) && tilesStatus === TilesStatus.Completed) {
      setShowCheckmark(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowCheckmark(false), CHECKMARK_DISPLAY_MS);
    }
  }, [tilesStatus]);

  const isProcessing =
    tilesStatus === TilesStatus.Pending || tilesStatus === TilesStatus.Processing;
  const isProcessingHighZoom =
    tilesStatus === TilesStatus.ProcessingHighZoom ||
    tilesStatus === TilesStatus.LowZoomReady;

  if (showCheckmark) {
    return (
      <Tooltip title="Shapefile tiles ready">
        <Badge
          count={
            <span style={{ display: "inline-flex" }}>
              <Icon path={mdiCheck} size={"16px"} color="var(--color-primary)" />
            </span>
          }
          style={{ marginLeft: 8 }}
        />
      </Tooltip>
    );
  }

  if (!isProcessing && !isProcessingHighZoom) {
    return null;
  }
  if (isProcessingHighZoom) {
    return (
      <Tooltip title="High zoom tiles are being processed for this shapefile">
        <Badge count={<IconSpinner size={14} />} style={{ marginLeft: 8 }} />
      </Tooltip>
    );
  }

  const tooltipTitle =
    tilesStatus === TilesStatus.Pending
      ? "Shapefile queued for processing"
      : "Processing tiles for this shapefile";

  return (
    <Tooltip title={tooltipTitle}>
      <Badge count={<IconSpinner size={14} />} style={{ marginLeft: 8 }} />
    </Tooltip>
  );
}
